import { Button, Center, Container, Group, Loader, Stack, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import type { Activity, Contract, PlannedItem, Space, WorkPackage } from '@/api/generated/models';
import {
  useAddActivityLine,
  useCreateActivity,
  useGetActivity,
  useUpdateActivity
} from '@/api/generated/activities/activities';
import { useGetContract, useListContracts } from '@/api/generated/contracts/contracts';
import { WorkPackageStatus } from '@/api/generated/models/workPackageStatus';
import {
  useGetSpace,
  useGetWorkPackage,
  useListAllWorkPackages,
  useListPlannedItems,
  useListSpaces
} from '@/api/generated/work-structure/work-structure';
import {
  BackButton,
  CardSection,
  FormActions,
  MutationButton,
  PageHeading,
  SelectField,
  TextareaField
} from '@/components/common';
import { formatWeekLabel } from '@/features/week-reports/weekReportDisplay';
import {
  buildSpaceBreadcrumb,
  buildSpaceOptions
} from '@/features/work-packages/workPackageDisplay';
import { getRecentIsoWeekOptions } from '@/utils/isoWeek';

/** Na mobitelu napomena kreće od jednog reda (visina ostalih polja) i raste dok se piše. */
const useNoteRows = () => {
  const isMobile = useMediaQuery('(max-width: 48em)', undefined, {
    getInitialValueInEffect: false
  });
  return isMobile ? { autosize: true, minRows: 1, maxRows: 5 } : { minRows: 2 };
};

const EditActivityForm = ({ activityId }: { activityId: string }) => {
  const { t } = useTranslation();
  const noteRows = useNoteRows();
  const navigate = useNavigate();
  const backTarget = `/activities/${activityId}`;

  const { data, isLoading } = useGetActivity(activityId, { query: { enabled: !!activityId } });
  const activity = data as Activity | undefined;

  const [note, setNote] = useState('');
  useEffect(() => {
    if (activity) {
      setNote(activity.note ?? '');
    }
  }, [activity]);

  const { data: contractData } = useGetContract(activity?.contractId ?? '', {
    query: { enabled: !!activity?.contractId }
  });
  const contract = contractData as Contract | undefined;

  const { data: workPackageData } = useGetWorkPackage(activity?.workPackageId ?? '', {
    query: { enabled: !!activity?.workPackageId }
  });
  const workPackage = workPackageData as WorkPackage | undefined;

  const { data: spaceData } = useGetSpace(activity?.spaceId ?? '', {
    query: { enabled: !!activity?.spaceId }
  });
  const space = spaceData as Space | undefined;

  const { data: objectSpacesData } = useListSpaces(space?.objectId ?? '', {
    query: { enabled: !!space?.objectId }
  });
  const objectSpaces: Space[] = objectSpacesData ?? [];
  const spaceBreadcrumb = buildSpaceBreadcrumb(activity?.spaceId, objectSpaces);

  const weekLabel = activity ? formatWeekLabel(activity.weekNumber, activity.reportYear) : '';

  const updateActivityMutation = useUpdateActivity({
    mutation: {
      onSuccess: () => {
        navigate(backTarget, { state: { savedMessage: t('activityDetail.updateSuccess') } });
      }
    }
  });

  if (isLoading || !activity) {
    return (
      <Container size="md" px={0}>
        <Center py={80}>
          <Loader size="sm" />
        </Center>
      </Container>
    );
  }

  return (
    <Container size="md" px={0}>
      <Stack gap={24}>
        <BackButton to={backTarget} />

        <PageHeading title={t('activityFormEdit.title')} />

        <CardSection title={t('newActivity.basicInfoTitle')}>
          <Stack gap={16} p={20}>
            <Group gap={16} grow>
              <SelectField
                label={t('newActivity.fieldContract')}
                disabled
                data={
                  contract?.id
                    ? [
                        {
                          value: contract.id,
                          label: contract.contractNumber ?? contract.name ?? ''
                        }
                      ]
                    : []
                }
                value={contract?.id ?? null}
                onChange={() => {}}
              />
              <SelectField
                label={t('newActivity.fieldWorkPackage')}
                disabled
                data={
                  workPackage?.id ? [{ value: workPackage.id, label: workPackage.name ?? '' }] : []
                }
                value={workPackage?.id ?? null}
                onChange={() => {}}
              />
            </Group>

            <Group gap={16} grow>
              <SelectField
                label={t('newActivity.fieldSpace')}
                disabled
                data={space?.id ? [{ value: space.id, label: spaceBreadcrumb }] : []}
                value={space?.id ?? null}
                onChange={() => {}}
              />
            </Group>

            <Group gap={16} grow>
              <SelectField
                label={t('newActivity.fieldWeek')}
                disabled
                data={weekLabel ? [{ value: weekLabel, label: weekLabel }] : []}
                value={weekLabel || null}
                onChange={() => {}}
              />
            </Group>

            <TextareaField
              label={t('activityDetail.note')}
              {...noteRows}
              value={note}
              onChange={(event) => setNote(event.currentTarget.value)}
            />
          </Stack>
        </CardSection>

        <FormActions>
          <Button variant="outline" onClick={() => navigate(backTarget)}>
            {t('common.cancel')}
          </Button>
          <MutationButton
            isPending={updateActivityMutation.isPending}
            isPaused={updateActivityMutation.isPaused}
            onClick={() =>
              updateActivityMutation.mutate({
                id: activityId,
                data: { note: note || undefined }
              })
            }
          >
            {t('activityFormEdit.submitButton')}
          </MutationButton>
        </FormActions>
      </Stack>
    </Container>
  );
};

const CreateActivityForm = () => {
  const { t } = useTranslation();
  const noteRows = useNoteRows();
  const navigate = useNavigate();

  const [workPackageId, setWorkPackageId] = useState<string | null>(null);
  const [contractId, setContractId] = useState<string | null>(null);
  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [weekValue, setWeekValue] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Nema zasebnog prekidača — način rada proizlazi izravno iz toga je li
  // odabran radni paket (ostala polja se tad izvode iz njega, vidi niže) ili
  // ne (ugovor/prostor/zona se tad biraju ručno).
  const mode: 'planned' | 'direct' = workPackageId ? 'planned' : 'direct';

  const { data: contractsData } = useListContracts({ query: { refetchOnMount: 'always' } });
  const contracts: Contract[] = contractsData ?? [];
  const contractOptions = contracts
    .filter((c) => c.id)
    .map((c) => ({ value: c.id as string, label: c.contractNumber ?? c.name ?? '' }));

  const { data: workPackagesData } = useListAllWorkPackages({
    query: { refetchOnMount: 'always' }
  });
  const workPackages: WorkPackage[] = workPackagesData ?? [];
  // Aktivnost se evidentira SAMO na Aktivnom radnom paketu (vidi ActivityService.create) —
  // Nacrt/Završen/Otkazan ne nudimo ovdje, ne samo da backend ionako odbije nego i da korisnik
  // ne odabere nešto što još nije u izvedbi. Kad je ugovor odabran, nude se samo paketi tog ugovora.
  const workPackageOptions = workPackages
    .filter(
      (wp) =>
        wp.id &&
        wp.status === WorkPackageStatus.ACTIVE &&
        (!contractId || wp.contractId === contractId)
    )
    .map((wp) => ({ value: wp.id as string, label: `${wp.code ?? ''} — ${wp.name ?? ''}` }));

  // Planirani način: radni paket nosi svoj contractId i spaceId izravno —
  // backend server-side isto tako validira preko workPackage.contractId, ne
  // preko lanca.
  const { data: selectedWorkPackageData } = useGetWorkPackage(workPackageId ?? '', {
    query: { enabled: mode === 'planned' && !!workPackageId }
  });
  const selectedWorkPackage = selectedWorkPackageData as WorkPackage | undefined;

  const { data: wpSpaceData } = useGetSpace(selectedWorkPackage?.spaceId ?? '', {
    query: { enabled: mode === 'planned' && !!selectedWorkPackage?.spaceId }
  });
  const wpSpace = wpSpaceData as Space | undefined;

  const { data: wpObjectSpacesData } = useListSpaces(wpSpace?.objectId ?? '', {
    query: { enabled: mode === 'planned' && !!wpSpace?.objectId }
  });
  const wpObjectSpaces: Space[] = wpObjectSpacesData ?? [];
  const wpSpaceBreadcrumb = buildSpaceBreadcrumb(selectedWorkPackage?.spaceId, wpObjectSpaces);

  const resolvedContractId = mode === 'planned' ? selectedWorkPackage?.contractId : contractId;
  const resolvedContract = contracts.find((c) => c.id === resolvedContractId);

  // Planirani način: ova dva polja su iste dropdown komponente kao u
  // izravnom načinu, samo onemogućena i unaprijed popunjena vrijednošću
  // razriješenom iz odabranog radnog paketa — svaka nudi točno jednu opciju
  // (trenutno razriješenu vrijednost) jer korisnik ovdje ništa ne bira.
  const plannedContractOptions = resolvedContract?.id
    ? [
        {
          value: resolvedContract.id,
          label: resolvedContract.contractNumber ?? resolvedContract.name ?? ''
        }
      ]
    : [];
  const plannedSpaceOptions = wpSpace?.id ? [{ value: wpSpace.id, label: wpSpaceBreadcrumb }] : [];

  // Radni paket nosi svoje planirane stavke — pri kreiranju aktivnosti se sve
  // one automatski kopiraju u aktivnost (realizedQty počinje od 0, korisnik ih
  // uređuje kasnije na pregledu aktivnosti), umjesto ručnog biranja svake.
  const { data: plannedItemsData } = useListPlannedItems(workPackageId ?? '', {
    query: { enabled: mode === 'planned' && !!workPackageId }
  });
  const plannedItems: PlannedItem[] = plannedItemsData ?? [];

  // Izravni način: ugovor -> prostor (bilo koje dubine u stablu objekta), ručno birano.
  const selectedDirectContract = contracts.find((c) => c.id === contractId);
  const { data: directSpacesData } = useListSpaces(selectedDirectContract?.objectId ?? '', {
    query: { enabled: mode === 'direct' && !!selectedDirectContract?.objectId }
  });
  const directSpaces: Space[] = directSpacesData ?? [];
  const directSpaceOptions = buildSpaceOptions(directSpaces);

  const weekOptions = useMemo(
    () =>
      getRecentIsoWeekOptions().map((option) => ({
        value: option.value,
        label: formatWeekLabel(option.week, option.year)
      })),
    []
  );

  // Odabir paketa postavlja i njegov ugovor; nakon brisanja paketa ugovor ostaje odabran (može se
  // obrisati zasebno), pa lista paketa i dalje nudi samo pakete tog ugovora.
  // Odabrani ugovor bez ijednog aktivnog radnog paketa — paket se ne može odabrati, aktivnost se
  // evidentira izravno na ugovoru (prostor se tada bira ručno).
  const contractHasNoWorkPackages =
    !workPackageId && !!contractId && workPackageOptions.length === 0;

  const handleWorkPackageChange = (value: string | null) => {
    setWorkPackageId(value);
    setSpaceId(null);
    if (value) {
      setContractId(workPackages.find((wp) => wp.id === value)?.contractId ?? null);
    }
  };

  const createActivityMutation = useCreateActivity();
  const addLineMutation = useAddActivityLine();

  const canSave = !!resolvedContractId && !!weekValue && (mode === 'direct' ? !!spaceId : true);

  const handleSave = async () => {
    if (!resolvedContractId || !weekValue) return;
    const [weekStr, yearStr] = weekValue.split('-');
    setSubmitError(null);
    try {
      const activity = await createActivityMutation.mutateAsync({
        data: {
          contractId: resolvedContractId,
          workPackageId: mode === 'planned' ? (workPackageId ?? undefined) : undefined,
          spaceId: mode === 'direct' ? (spaceId ?? undefined) : undefined,
          weekNumber: Number(weekStr),
          reportYear: Number(yearStr),
          note: note || undefined
        }
      });
      const activityId = (activity as { id?: string })?.id;
      if (!activityId) throw new Error('missing id');

      if (mode === 'planned' && plannedItems.length > 0) {
        await Promise.allSettled(
          plannedItems
            .filter((item) => item.id)
            .map((item) =>
              addLineMutation.mutateAsync({
                id: activityId,
                data: { plannedItemId: item.id as string, realizedQty: 0 }
              })
            )
        );
      }

      navigate(`/activities/${activityId}`, {
        state: { savedMessage: t('activityDetail.createSuccess') }
      });
    } catch {
      setSubmitError(t('newActivity.saveError'));
    }
  };

  return (
    <Container size="md" px={0}>
      <Stack gap={24}>
        <BackButton to="/activities" />

        <PageHeading title={t('newActivity.title')} subtitle={t('newActivity.subtitle')} />

        <CardSection title={t('newActivity.basicInfoTitle')}>
          <Stack gap={16} p={20}>
            <Group gap={16} grow align="flex-start">
              <Stack gap={4}>
                <SelectField
                  label={t('newActivity.fieldWorkPackage')}
                  clearable
                  disabled={contractHasNoWorkPackages}
                  data={workPackageOptions}
                  value={workPackageId}
                  onChange={handleWorkPackageChange}
                />
                {contractHasNoWorkPackages && (
                  <Text fz={11} style={{ color: 'var(--color-status-danger)' }}>
                    {t('newActivity.noWorkPackagesForContract')}
                  </Text>
                )}
              </Stack>
              {mode === 'planned' ? (
                <SelectField
                  label={t('newActivity.fieldContract')}
                  disabled
                  data={plannedContractOptions}
                  value={resolvedContract?.id ?? null}
                  onChange={() => {}}
                />
              ) : (
                <SelectField
                  label={t('newActivity.fieldContract')}
                  required
                  clearable
                  data={contractOptions}
                  value={contractId}
                  onChange={(value) => {
                    setContractId(value);
                    setSpaceId(null);
                  }}
                />
              )}
            </Group>

            <Group gap={16} grow>
              {mode === 'planned' ? (
                <SelectField
                  label={t('newActivity.fieldSpace')}
                  disabled
                  data={plannedSpaceOptions}
                  value={wpSpace?.id ?? null}
                  onChange={() => {}}
                />
              ) : (
                <SelectField
                  label={t('newActivity.fieldSpace')}
                  required
                  disabled={!contractId}
                  data={directSpaceOptions}
                  value={spaceId}
                  onChange={setSpaceId}
                />
              )}
            </Group>

            <Group gap={16} grow>
              <SelectField
                label={t('newActivity.fieldWeek')}
                required
                data={weekOptions}
                value={weekValue}
                onChange={setWeekValue}
              />
            </Group>

            <TextareaField
              label={t('activityDetail.note')}
              {...noteRows}
              value={note}
              onChange={(event) => setNote(event.currentTarget.value)}
            />
          </Stack>
        </CardSection>

        {submitError && (
          <Text fz={13} style={{ color: 'var(--color-status-danger)' }}>
            {submitError}
          </Text>
        )}

        <FormActions>
          <Button variant="outline" onClick={() => navigate('/activities')}>
            {t('common.cancel')}
          </Button>
          <MutationButton
            isPending={createActivityMutation.isPending}
            isPaused={createActivityMutation.isPaused}
            disabled={!canSave}
            onClick={handleSave}
          >
            {t('newActivity.save')}
          </MutationButton>
        </FormActions>
      </Stack>
    </Container>
  );
};

export const NewActivityPage = () => {
  const { id } = useParams<{ id?: string }>();
  return id ? <EditActivityForm activityId={id} /> : <CreateActivityForm />;
};
